<template>
  <section
    v-if="visible"
    class="question-sets-promo-card"
  >
    <span
      class="sparkles"
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 24 24"
        width="36"
        height="36"
      >
        <defs>
          <linearGradient
            id="question-sets-promo-sparkle"
            x1="0"
            y1="0"
            x2="1"
            y2="1"
          >
            <stop
              offset="0%"
              stop-color="#A78BFA"
            />
            <stop
              offset="50%"
              stop-color="#60A5FA"
            />
            <stop
              offset="100%"
              stop-color="#F472B6"
            />
          </linearGradient>
        </defs>
        <path
          class="star"
          fill="url(#question-sets-promo-sparkle)"
          d="M10 2c.4 3.9 2.1 5.6 6 6-3.9.4-5.6 2.1-6 6-.4-3.9-2.1-5.6-6-6 3.9-.4 5.6-2.1 6-6z"
        />
        <path
          class="star star-small"
          fill="url(#question-sets-promo-sparkle)"
          d="M18 13c.25 2.35 1.25 3.35 3.6 3.6-2.35.25-3.35 1.25-3.6 3.6-.25-2.35-1.25-3.35-3.6-3.6 2.35-.25 3.35-1.25 3.6-3.6z"
        />
        <path
          class="star star-tiny"
          fill="url(#question-sets-promo-sparkle)"
          d="M19 2.5c.15 1.4.75 2 2.1 2.1-1.35.15-1.95.75-2.1 2.1-.15-1.35-.75-1.95-2.1-2.1 1.35-.1 1.95-.7 2.1-2.1z"
        />
      </svg>
    </span>
    <div class="copy">
      <div class="headline text-lg font-semibold text-primary-900">
        Psst… turn your notes into practice questions
        <span class="beta text-xs font-semibold">Beta</span>
      </div>
      <p class="text-sm font-regular">
        Snap a photo of your notes, drop in a text file, or just type a topic, and get practice questions made just for you,
        plus feedback on what to brush up on. It's a beta, so let's keep it between us.
      </p>
    </div>
    <router-link
      class="button-primary text-md font-semibold try-it"
      :to="{ name: 'questionSets#index' }"
      @click="track('selectedMyQuestions', { source: 'promo_card' })"
    >
      Try it
    </router-link>
    <button
      class="button-reset dismiss"
      aria-label="Hide this"
      @click="dismiss"
    >
      ×
    </button>
  </section>
</template>

<script>
export default {
  data() {
    return {
      dismissed: false,
    };
  },
  computed: {
    storageKey() {
      return `question-sets-promo-dismissed-${this.$store.getters['user/get']?.id}`;
    },
    visible() {
      return !this.dismissed && this.$store.getters['questionSets/visible'];
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(visible) {
        if (visible) this.track('viewedMyQuestionsEntry', { source: 'promo_card' });
      },
    },
    storageKey: {
      immediate: true,
      handler(key) {
        try {
          this.dismissed = window.localStorage.getItem(key) === 'true';
        } catch {
          this.dismissed = false;
        }
      },
    },
  },
  methods: {
    track(event, params = {}) {
      this.$analytics.events[event]({ ...params, consentPending: this.$store.getters['questionSets/consentPending'] });
    },
    // Remembered per user in this browser
    dismiss() {
      this.track('dismissedMyQuestionsPromo');
      this.dismissed = true;
      try {
        window.localStorage.setItem(this.storageKey, 'true');
      } catch {
        // Storage can be blocked; the card just comes back next visit
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .question-sets-promo-card {
    align-items: center;
    background: linear-gradient(120deg, #F5F3FF 0%, #EFF6FF 55%, #FDF2F8 100%);
    border: 1px solid #DDD6FE;
    border-radius: var(--radius-xl);
    display: flex;
    gap: rem-calc(16);
    margin-bottom: rem-calc(24);
    padding: rem-calc(20) rem-calc(48) rem-calc(20) rem-calc(20);
    position: relative;

    @include breakpoint(small) {
      align-items: flex-start;
      flex-wrap: wrap;
      padding-right: rem-calc(20);
    }

    .sparkles {
      display: flex;
      filter: drop-shadow(0 0 6px rgba(167, 139, 250, .6));
      flex-shrink: 0;
    }

    .star {
      animation: promo-twinkle 2.4s ease-in-out infinite;
      transform-box: fill-box;
      transform-origin: center;
    }

    .star-small {
      animation-delay: .8s;
    }

    .star-tiny {
      animation-delay: 1.6s;
    }

    .copy {
      flex: 1;
      min-width: rem-calc(220);

      p {
        color: var(--text-tertiary);
        margin: rem-calc(4) 0 0;
      }
    }

    .beta {
      border: 1px solid #C4B5FD;
      border-radius: 999px;
      color: #7C3AED;
      letter-spacing: .04em;
      margin-left: rem-calc(6);
      padding: 0 rem-calc(8);
      text-transform: uppercase;
      vertical-align: middle;
    }

    .try-it {
      align-items: center;
      display: inline-flex;
      flex-shrink: 0;
      text-decoration: none;
    }

    .dismiss {
      color: var(--text-tertiary);
      cursor: pointer;
      font-size: rem-calc(20);
      line-height: 1;
      position: absolute;
      right: rem-calc(16);
      top: rem-calc(12);
    }

    @media (prefers-reduced-motion: reduce) {
      .star {
        animation: none;
      }
    }
  }

  @keyframes promo-twinkle {
    0%, 100% {
      opacity: 1;
      transform: scale(1) rotate(0deg);
    }

    50% {
      opacity: .45;
      transform: scale(.7) rotate(20deg);
    }
  }
</style>
